import * as TableauUI from '@tableau/tableau-ui';
import * as React from 'react';
import { makeTestId } from '../../utils/Declarative';
import { PropsAndButtonAttrs, PropsAndElementAttrs, PropsAndInputAttrs } from '../../utils/ReactHelpers';
import { ButtonProps } from '../Button/ButtonWidget';
import { MenuButtonProps, validateMenuButtonProps } from '../Button/MenuButtonWidget';
import { TextFieldProps } from './TextFieldWidget';

export type TextFieldGroupProps = {
  /**
   * The primary button.
   *
   * @example
   *   button={<ButtonWidget buttonType={ButtonType.Primary} children='Create' />}
   */
  button?: React.ReactElement<PropsAndButtonAttrs<ButtonProps>>;

  /** This component does not use `children`. */
  children?: never;

  /**
   * The popup button. `children`, `aria-label` or `aria-labelledby` must be set on its props, otherwise an error will be thrown.
   *
   * @example
   *   menuButton={<MenuButtonWidget children='Download' aria-controls='your-menu-id' kind='primary' />}
   */
  menuButton?: React.ReactElement<PropsAndButtonAttrs<MenuButtonProps>>;

  /** Added to the root `<div>` with the suffix `-TextFieldGroupWidget` */
  testId?: string;

  /**
   * The text field.
   *
   * @example
   *   textField={<TextFieldWidget label='Hierarchy Name' />}
   */
  textField: React.ReactElement<PropsAndInputAttrs<TextFieldProps>>;
};

/**
 * A component which visually groups a `TextFieldWidget` with a `ButtonWidget` and/or a `MenuButtonWidget`.
 *
 * All props not defined in `TextFieldGroupProps` will be rested to the root element.
 */
export const TextFieldGroupWidget: React.FC<PropsAndElementAttrs<TextFieldGroupProps>> = ({ button, menuButton, testId, textField, ...htmlAttrs }) => {
  if (menuButton) {
    validateMenuButtonProps(menuButton.props);
  }

  return <TableauUI.TextFieldGroup
    data-tb-test-id={makeTestId(testId, TextFieldGroupWidget.displayName)}
    {...htmlAttrs}
    textField={textField}
    button={button!}
    children={menuButton}
  />;
};
TextFieldGroupWidget.displayName = 'TextFieldGroupWidget';
